import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { FC, FormEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import ChannelsList from '@/pages/EditorialDistributionQueue/components/ChannelsList';
import FormPageGroupSwitch from '@/pages/EditorialDistributionQueue/components/FormPageGroupSwitch';
import {
  ChannelGroupResource,
  EditorialFacebookPostResource,
  EditorialQueuePage,
} from '@/types/resources';
import { api } from '@/utils/api';
import { EditorialFacebookPostNetwork } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';

const Dialog = styled(StyledDialog)`
  width: 464px;
  text-align: left;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const Heading = styled(Text)`
  text-align: center;
  padding: 56px 32px 24px;
`;

const Body = styled.div`
  padding: 0 32px 56px;
`;

const Footer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  justify-content: flex-end;
  padding: 24px 32px 32px;
  box-shadow: 0px -1px 8px 0px #2222220f;
`;

const SwitchWrap = styled(Larger)`
  margin-top: -8px;
`;

type FormDataT = {
  channel_id: null | number;
  channel_group_id: null | number;
  option: string;
  network: EditorialFacebookPostNetwork;
  message: string;
};

interface Props {
  onClose: VoidFunction;
  post: EditorialFacebookPostResource;
}

const RepostModal: FC<Props> = ({ onClose, post }) => {
  const [isChannelGroups, setIsChannelGroups] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    clearErrors,
    control,
    watch,
    reset,
    resetField,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      channel_id: null,
      channel_group_id: null,
      option: 'append',
      message: post.message ?? '',
      network: EditorialFacebookPostNetwork.FACEBOOK,
    },
  });

  const { data: channelGroups } = useQuery(
    ['channel_groups'],
    () =>
      api.get<never, ChannelGroupResource[]>(`/v1/distribution/channel-groups`),
    {
      staleTime: 1000 * 60,
    },
  );

  const network = watch('network');

  const { data: channelOptions } = useQuery(
    [
      'editorial_distribution_queue_channels',
      {
        ...(network === EditorialFacebookPostNetwork.FACEBOOK && {
          hasFacebook: true,
        }),
        ...(network === EditorialFacebookPostNetwork.YOUTUBE_SHORTS && {
          hasYoutube: true,
        }),
      },
    ],
    () =>
      api.get<never, EditorialQueuePage[]>('/v1/editorial/queue/channels', {
        params: {
          ...(network === EditorialFacebookPostNetwork.FACEBOOK && {
            'filter[hasFacebook]': '1',
          }),
          ...(network === EditorialFacebookPostNetwork.YOUTUBE_SHORTS && {
            'filter[hasYoutube]': '1',
          }),
        },
      }),
    {
      staleTime: 120_000,
      select: (data) =>
        data?.map((channel) => ({
          label: channel.name,
          value: channel.id,
        })),
    },
  );

  const channelGroupId = watch('channel_group_id');

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(async (data) => {
      try {
        const { error, channels } = await api.post<
          typeof data,
          { error?: string; channels?: Array<{ id: number; name: string }> }
        >(`/v1/editorial/facebook-posts/${post.id}/repost`, data);

        if (error) {
          return alerts.error('Something went wrong!', error);
        }

        await queryClient.invalidateQueries([
          'editorial_distribution_schedules',
        ]);

        onClose();

        alerts.success(
          'Post has been successfully reposted to these pages',
          channels?.map((channel) => channel.name).join('\n'),
        );
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  useEffect(() => {
    reset();
  }, [isChannelGroups, reset]);

  useEffect(() => {
    resetField('channel_id');
    resetField('channel_group_id');
  }, [network, resetField]);

  return (
    <Modal zIndex={50}>
      <Dialog
        onSubmit={onSubmit}
        as={motion.form}
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Heading $type="heading2">Repost</Heading>
        <CloseButton onClick={onClose} type="button">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Body>
          <FormLayout>
            <FormField<FormDataT, EditorialFacebookPostNetwork>
              control={control}
              name="network"
              type="select"
              label="Network"
              options={[
                {
                  value: EditorialFacebookPostNetwork.FACEBOOK,
                  label: 'Facebook',
                },
                {
                  value: EditorialFacebookPostNetwork.YOUTUBE_SHORTS,
                  label: 'Youtube',
                },
              ]}
            />
            <FormField<FormDataT>
              control={control}
              name="option"
              type="select"
              label="Option"
              options={[
                {
                  value: 'append',
                  label: 'Append post to the end of the list',
                },
                {
                  value: 'prepend',
                  label: 'Prepend post to the start of the list',
                },
              ]}
            />
            <SwitchWrap>
              <FormPageGroupSwitch
                isChannelGroups={isChannelGroups}
                setIsChannelGroups={setIsChannelGroups}
              />
            </SwitchWrap>
            {isChannelGroups ? (
              <>
                <FormField<FormDataT, number>
                  control={control}
                  type="select"
                  name="channel_group_id"
                  label="Page Group"
                  options={
                    channelGroups?.map((group) => {
                      return {
                        label: group.name,
                        value: group.id,
                      };
                    }) ?? []
                  }
                />
                {!!channelGroupId && !!channelGroups?.length && (
                  <Larger>
                    <ChannelsList
                      currentGroup={channelGroups.find(
                        (group) => group.id === channelGroupId,
                      )}
                    />
                  </Larger>
                )}
              </>
            ) : (
              <FormField<FormDataT, number>
                key="page_name"
                label="Page Name"
                type="select"
                control={control}
                name="channel_id"
                options={channelOptions ?? []}
              />
            )}
            <FormField<FormDataT>
              label="Message"
              control={control}
              name="message"
              type="textarea"
            />
          </FormLayout>
        </Body>
        <Footer>
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
            Repost
          </Button>
          <Button type="button" color="secondary" onClick={onClose}>
            Cancel
          </Button>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default RepostModal;
